import React from 'react';
import { Lock, Phone, ShieldCheck, User } from 'lucide-react';
import { motion } from 'framer-motion';

const EASE = [0.22, 1, 0.36, 1];

const STEP_ORDER = ['phone', 'otp', 'prospect'];
const STEP_LABELS = ['Verify phone', 'Enter code', 'Your details'];

const UnlockPhase = ({
  unlockStep,
  unlockPhone,
  setUnlockPhone,
  handleSendOtp,
  otpSending,
  otpResendCooldownMs,
  otpCooldownSeconds,
  otpStatusMessage,
  otpCode,
  setOtpCode,
  handleVerifyOtp,
  otpChecking,
  prospectName,
  setProspectName,
  prospectEmail,
  setProspectEmail,
  prospectRelationship,
  setProspectRelationship,
  prospectOptIn,
  setProspectOptIn,
  prospectConsentAccepted,
  setProspectConsentAccepted,
  handleSubmitProspect,
  prospectSubmitting,
  unlocking,
  unlockError
}) => {
  const activeIndex = Math.max(0, STEP_ORDER.indexOf(unlockStep));

  return (
    <motion.div
      className="vlg-phase"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -6 }}
      transition={{ duration: 0.32, ease: EASE }}
    >
      <div className="vlg-unlock">
        <div className="vlg-steps" role="list" aria-label="Unlock progress">
          {STEP_LABELS.map((label, index) => {
            const state = index < activeIndex ? 'is-done' : index === activeIndex ? 'is-active' : '';
            return (
              <span
                key={label}
                role="listitem"
                className={`vlg-step ${state}`.trim()}
                aria-current={index === activeIndex ? 'step' : undefined}
              >
                <span className="vlg-step__bar" aria-hidden="true" />
                <span className="vlg-step__label">{index + 1}. {label}</span>
              </span>
            );
          })}
        </div>

        {unlockStep === 'phone' && (
          <div className="vlg-stepcard">
            <h3 className="vlg-stepcard__title"><Phone size={19} aria-hidden="true" /> Verify your phone</h3>
            <p className="vlg-stepcard__sub">
              We&apos;ll text a one-time code to keep your competitor report private to you.
            </p>
            <div className="vlg-field">
              <label className="vlg-label" htmlFor="vlg-phone">Phone number</label>
              <input
                id="vlg-phone"
                type="tel"
                className="vlg-input"
                placeholder="(555) 555-5555"
                value={unlockPhone}
                onChange={(event) => setUnlockPhone(event.target.value)}
              />
            </div>
            <div className="vlg-actions">
              <button className="vlg-btn" type="button" onClick={handleSendOtp} disabled={otpSending || otpResendCooldownMs > 0}>
                {otpSending ? 'Sending code…' : otpResendCooldownMs > 0 ? `Retry in ${otpCooldownSeconds}s` : 'Send verification code'}
              </button>
            </div>
          </div>
        )}

        {unlockStep === 'otp' && (
          <div className="vlg-stepcard">
            <h3 className="vlg-stepcard__title"><ShieldCheck size={19} aria-hidden="true" /> Enter your code</h3>
            <p className="vlg-stepcard__sub">Enter the verification code we just sent to your phone.</p>
            {otpStatusMessage && <p className="vlg-note vlg-note--success">{otpStatusMessage}</p>}
            <div className="vlg-field">
              <label className="vlg-label" htmlFor="vlg-otp">Verification code</label>
              <input
                id="vlg-otp"
                type="text"
                inputMode="numeric"
                className="vlg-input"
                placeholder="Enter code"
                value={otpCode}
                onChange={(event) => setOtpCode(event.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
              />
            </div>
            <div className="vlg-actions">
              <button className="vlg-btn" type="button" onClick={handleVerifyOtp} disabled={otpChecking}>
                {otpChecking ? 'Verifying…' : 'Verify code'}
              </button>
              <button className="vlg-btn vlg-btn--ghost" type="button" onClick={handleSendOtp} disabled={otpSending || otpResendCooldownMs > 0}>
                {otpSending ? 'Resending…' : otpResendCooldownMs > 0 ? `Resend in ${otpCooldownSeconds}s` : 'Resend code'}
              </button>
            </div>
          </div>
        )}

        {unlockStep === 'prospect' && (
          <div className="vlg-stepcard">
            <h3 className="vlg-stepcard__title"><User size={19} aria-hidden="true" /> A few quick details</h3>
            <p className="vlg-stepcard__sub">This personalizes your report and confirms you&apos;re connected to the business.</p>

            <div className="vlg-usenote" role="note" aria-label="How your info is used">
              <strong>How we use this</strong>
              <ul>
                <li>Confirm you&apos;re connected to the business in the report.</li>
                <li>Personalize the recommended actions in your unlocked report.</li>
                <li>Send optional follow-ups only if you opt in.</li>
              </ul>
            </div>

            <div className="vlg-fieldgrid">
              <div className="vlg-field">
                <label className="vlg-label" htmlFor="vlg-name">Name</label>
                <input
                  id="vlg-name"
                  type="text"
                  className="vlg-input"
                  placeholder="Your name"
                  value={prospectName}
                  onChange={(event) => setProspectName(event.target.value)}
                />
              </div>

              <div className="vlg-field">
                <label className="vlg-label" htmlFor="vlg-email">Email</label>
                <input
                  id="vlg-email"
                  type="email"
                  className="vlg-input"
                  placeholder="you@business.com"
                  value={prospectEmail}
                  onChange={(event) => setProspectEmail(event.target.value)}
                />
              </div>

              <div className="vlg-field">
                <label className="vlg-label" htmlFor="vlg-relationship">Relationship</label>
                <select
                  id="vlg-relationship"
                  className="vlg-select"
                  value={prospectRelationship}
                  onChange={(event) => setProspectRelationship(event.target.value)}
                >
                  <option value="owner_manager">I&apos;m the business owner or manager</option>
                  <option value="service_provider">I provide services to this business</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="vlg-consent" aria-label="Consent options">
              <label className="vlg-consent__row">
                <input
                  type="checkbox"
                  checked={prospectOptIn}
                  onChange={(event) => setProspectOptIn(event.target.checked)}
                />
                <span>Send me optional updates on score changes and competitor moves.</span>
              </label>

              <label className="vlg-consent__row is-required">
                <input
                  type="checkbox"
                  checked={prospectConsentAccepted}
                  onChange={(event) => setProspectConsentAccepted(event.target.checked)}
                />
                <span>
                  I agree to the Terms of Service and Privacy Policy, and consent to data collection and use to unlock this report.
                </span>
              </label>
            </div>

            <div className="vlg-actions">
              <button className="vlg-btn" type="button" onClick={handleSubmitProspect} disabled={prospectSubmitting || unlocking}>
                {prospectSubmitting || unlocking ? 'Unlocking…' : 'Unlock report'}
              </button>
            </div>
          </div>
        )}

        {unlockError && <p className="vlg-note vlg-note--error">{unlockError}</p>}
        <p className="vlg-note" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Lock size={13} aria-hidden="true" /> Your details unlock the full competitor ranking and AI recommendations.
        </p>
      </div>
    </motion.div>
  );
};

export default UnlockPhase;
